import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Box,
  Toolbar,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Typography,
  useMediaQuery,
  Tooltip,
  DialogActions
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  Assessment as AssessmentIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  AccountCircle as ProfileIcon,
  Menu as MenuIcon,
  CalendarMonth as CalendarIcon,
  CalendarMonth,
  Description as DescriptionIcon,
  Group as GroupsIcon
} from "@mui/icons-material";
import { useAuth } from "../auth/AuthContext";
import { useThemeToggle } from "../context/ThemeContext";
import colors from "../constants/color";
import logo from "../assets/logo.png";
import axios from "axios";
import { url } from "../constants/url";

const expandedWidth = 240;
const collapsedWidth = 68;

const Navbar = ({ open, onToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, currentUser } = useAuth();
  const { mode } = useThemeToggle();
  const palette = colors[mode];
  const isMobile = useMediaQuery("(max-width:800px)");
  const isSmallScreen = useMediaQuery("(max-width:600px)");

  const [logoutPopOpen, setLogoutPopOpen] = useState(false);
  const [userRole, setUserRole] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(open);

  // ✅ Sync with parent state
  useEffect(() => {
    setDrawerOpen(open);
  }, [open]);

  // ✅ Handle drawer toggle - improved for all screen sizes
  const handleToggle = () => {
    const newState = !drawerOpen;
    setDrawerOpen(newState);
    onToggle(newState);
  };

  const handleCloseMobileDrawer = () => {
    if (isMobile && drawerOpen) {
      const newState = false;
      setDrawerOpen(newState);
      onToggle(newState);
    }
  };

  // ✅ Close mobile drawer on route change
  useEffect(() => {
    if (isMobile) {
      handleCloseMobileDrawer();
    }
  }, [location.pathname]);

  // ✅ Fetch user role
  useEffect(() => {
    const fetchRole = async () => {
      if (!currentUser?.email) return;
      try {
        const res = await axios.get(
          `${url}/api/users/get-user-role/${currentUser.email}`
        );
        setUserRole(res.data.role);
      } catch (error) {
        console.error("Failed to fetch user role:", error);
      }
    };
    fetchRole();
  }, [currentUser]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Logout failed", error);
    }
  };

  // ✅ Role-based menu configuration - Profile added to all roles
  const baseMenu = [
    { text: "Dashboard", icon: <DashboardIcon />, path: "/dashboard" },
    { text: "Leads", icon: <PeopleIcon />, path: "/leads" },
  ];

  const roleMenus = {
    superAdmin: [
      ...baseMenu,
      { text: "Company", icon: <BusinessIcon />, path: "/add-company" },
      { text: "Reports", icon: <AssessmentIcon />, path: "/reports" },
      { text: "Settings", icon: <SettingsIcon />, path: "/settings" },
      { text: "Profile", icon: <ProfileIcon />, path: "/profile" },
    ],
    admin: [
      ...baseMenu,
      { text: "Team Collaboration", icon: <GroupsIcon />, path: "/team-collaboration" },
      { text: "Meetings", icon: <CalendarMonth />, path: "/meetings" },
      { text: "Quotations", icon: <DescriptionIcon />, path: "/quotations" },
      { text: "Company", icon: <BusinessIcon />, path: "/add-company" },
      { text: "Reports", icon: <AssessmentIcon />, path: "/reports" },
      { text: "Calendar", icon: <CalendarIcon />, path: "/calendar" },
      { text: "Settings", icon: <SettingsIcon />, path: "/settings" },
      { text: "Profile", icon: <ProfileIcon />, path: "/profile" },
    ],
    teamMember: [
      ...baseMenu,
      { text: "Team Collaboration", icon: <GroupsIcon/>, path: "/team-collaboration" },
      { text: "Meetings", icon: <CalendarMonth />, path: "/meetings" },
      { text: "Quotations", icon: <DescriptionIcon />, path: "/quotations" },
      { text: "Reports", icon: <AssessmentIcon />, path: "/reports" },
      { text: "Calendar", icon: <CalendarIcon />, path: "/calendar" },
      { text: "Settings", icon: <SettingsIcon />, path: "/settings" },
      { text: "Profile", icon: <ProfileIcon />, path: "/profile" },
    ],
  };

  const menuItems = roleMenus[userRole] || [...baseMenu, { text: "Profile", icon: <ProfileIcon />, path: "/profile" }];

  // ✅ Drawer Content
  const drawerContent = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: palette.sidebarBg,
        overflow: "hidden",
      }}
    >
      {/* Logo + Toggle */}
      <Toolbar
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: drawerOpen ? "space-between" : "center",
          px: drawerOpen ? 2 : 0,
          py: 0.5,
          borderBottom: `1px solid ${palette.divider}`,
          background: mode === "dark"
            ? "rgba(255, 255, 255, 0.04)"
            : "rgba(0,0,0,0.03)",
          minHeight: "64px !important",
        }}
      >
        {drawerOpen ? (
          <>
            <Box
              component="img"
              src={logo}
              alt="LeadsToSell"
              onClick={() => navigate("/dashboard")}
              sx={{
                height: 38,
                width: "auto",
                objectFit: "contain",
                cursor: "pointer",
                ml: -5,
                transition: "all 0.3s ease",
                filter: mode === "dark" 
                  ? "drop-shadow(0 0 5px rgba(255,255,255,0.3))" 
                  : "drop-shadow(0 0 3px rgba(0,0,0,0.2))",
              }}
            />

            {/* Close icon - Only show on desktop */}
            {!isMobile && (
              <IconButton
                onClick={handleToggle}
                sx={{
                  color: palette.iconDefault,
                  ml: -4,
                  "&:hover": {
                    color: palette.accent,
                    transform: "scale(1.1)",
                  },
                  transition: "all 0.25s ease",
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
            )}
          </>
        ) : (
          // Expanded icon - Only show on desktop when collapsed
          !isMobile && (
            <IconButton
              onClick={handleToggle}
              sx={{
                color: palette.iconDefault,
                "&:hover": {
                  color: palette.accent,
                  transform: "scale(1.1)",
                },
                transition: "all 0.25s ease",
              }}
            >
              <ChevronRightIcon />
            </IconButton>
          )
        )}
      </Toolbar>

      {/* Menu List - All navigation items including Profile */}
      <List 
        sx={{ 
          flexGrow: 1, 
          mt: 1,
          overflow: 'auto',
          '&::-webkit-scrollbar': {
            width: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: palette.divider,
            borderRadius: '2px',
          },
        }}
      >
        {menuItems.map((item) => {
          const selected = location.pathname === item.path;
          const listItemContent = (
            <ListItemButton
              component={Link}
              to={item.path}
              onClick={isMobile ? handleCloseMobileDrawer : undefined}
              sx={{
                justifyContent: drawerOpen ? "initial" : "center",
                px: drawerOpen ? 2.5 : 1.5,
                py: 1.3,
                mx: 1,
                borderRadius: 2,
                background: selected
                  ? `linear-gradient(90deg, ${palette.accentLight})`
                  : "transparent",
                color: selected ? palette.textPrimary : palette.textSecondary,
                "&:hover": {
                  background: palette.accentLight,
                  color: palette.textPrimary,
                  "& .MuiListItemIcon-root": {
                    color: colors.white,
                  },
                },
                transition: "all 0.25s ease-in-out",
                minHeight: "48px",
                mb: 0.5,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: drawerOpen ? 2 : "auto",
                  justifyContent: "center",
                  color: selected ? colors.white : colors.white,
                  transition: "color 0.2s ease",
                }}
              >
                {item.icon}
              </ListItemIcon>
              {drawerOpen && (
                <ListItemText
                  primary={
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: selected ? 600 : 400,
                        letterSpacing: 0.3,
                      }}
                    >
                      {item.text}
                    </Typography>
                  }
                />
              )}
            </ListItemButton>
          );

          return (
            <ListItem key={item.text} disablePadding sx={{ display: "block" }}>
              {!drawerOpen && !isMobile ? (
                <Tooltip title={item.text} placement="right" arrow enterDelay={500}>
                  {listItemContent}
                </Tooltip>
              ) : (
                listItemContent
              )}
            </ListItem>
          );
        })}
      </List>
    </Box>
  );

  return (
    <Drawer
      variant={isMobile ? "temporary" : "permanent"}
      open={drawerOpen}
      onClose={handleCloseMobileDrawer}
      anchor="left"
      sx={{
        width: drawerOpen ? expandedWidth : collapsedWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: drawerOpen ? expandedWidth : collapsedWidth,
          boxSizing: "border-box",
          backgroundColor: palette.sidebarBg,
          color: palette.textPrimary,
          borderRight: "none",
          boxShadow: isMobile ? palette.shadow : "none",
          transition: "width 0.3s ease-in-out, transform 0.3s ease-in-out",
          overflow: "hidden",
          height: isMobile ? '100%' : 'calc(100% - 0px)',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default Navbar;